import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/App';
import { Video, Calendar, Clock, Lock, DollarSign, Key, Play, ShoppingCart, Library } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReplayPlayer from '@/components/ReplayPlayer';
import { toast } from 'sonner';
import { useAudio } from '@/contexts/AudioContext';
import TierPaymentModal from '@/components/TierPaymentModal';
import StreamPurchaseModal from '@/components/StreamPurchaseModal';
import TruncatedText from '@/components/TruncatedText';
import { Pagination } from '@/components/Pagination';
import { useTranslatedMap, TranslatableText } from '@/contexts/LanguageContext';

const LiveStreamPage = ({ user }) => {
  const navigate = useNavigate();
  const [streams, setStreams] = useState([]);
  const tr = useTranslatedMap(streams.flatMap((s) => [s.title, s.description]));
  const [loading, setLoading] = useState(true);
  const [membershipType, setMembershipType] = useState(user?.membership_tier || null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTierPaymentModal, setShowTierPaymentModal] = useState(false);
  const [purchaseModal, setPurchaseModal] = useState({ open: false, stream: null, type: 'live' });
  const [playingReplay, setPlayingReplay] = useState(null);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [recordedPage, setRecordedPage] = useState(1);
  const PER_PAGE = 10;
  const { pauseForOtherAudio, resumeAfterOtherAudio } = useAudio();
  const iframeRefs = useRef([]);

  useEffect(() => {
    fetchStreams();
    fetchMembershipStatus();
  }, []);

  const fetchMembershipStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/membership`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembershipType(response.data.membership_tier || response.data.membership_type);
    } catch (error) {
      console.error('Failed to fetch membership status');
    }
  };

  // Check if user has live stream access (Gold, Platinum, or free_comp only)
  const hasLiveStreamAccess = ['gold', 'platinum', 'free_comp'].includes(membershipType);
  const isViewOnly = !hasLiveStreamAccess;

  const fetchStreams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/live-streams?per_page=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStreams(response.data.streams || response.data || []);
    } catch (error) {
      if (error.response?.status === 403) {
        setStreams([]);
      } else {
        toast.error('Failed to load streams');
      }
    } finally {
      setLoading(false);
    }
  };

  const liveStreams = streams.filter(s => s.is_live);
  const now = Date.now();
  const TWO_WEEKS = 14 * 24 * 60 * 60 * 1000;
  const startedAt = (s) => {
    if (!s.scheduled_date) return null;
    const t = new Date(s.scheduled_date).getTime();
    return Number.isNaN(t) ? null : t;
  };
  const isFuture = (s) => {
    const t = startedAt(s);
    return t !== null && t > now;
  };
  const upcomingStreams = streams.filter(s => !s.is_live && isFuture(s));
  // Recent streams stay on this page for two weeks, then the Replay Archive keeps them
  const recordedStreams = streams.filter(s => {
    if (s.is_live || isFuture(s)) return false;
    const t = startedAt(s);
    return t === null || (now - t) <= TWO_WEEKS;
  });

  const addToCalendar = (stream) => {
    if (!stream.scheduled_date) {
      toast.error('This stream does not have a scheduled date');
      return;
    }

    const startDateTime = new Date(stream.scheduled_date);
    const endDateTime = new Date(startDateTime);
    endDateTime.setHours(endDateTime.getHours() + 1);

    const formatICSDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//11/11 Collective Connection//EN
BEGIN:VEVENT
UID:${stream.id}@eleven-collective.com
DTSTAMP:${formatICSDate(new Date())}
DTSTART:${formatICSDate(startDateTime)}
DTEND:${formatICSDate(endDateTime)}
SUMMARY:${stream.title}
DESCRIPTION:${stream.description}\\n\\nJoin: ${stream.embed_url}
LOCATION:Online
URL:${stream.embed_url}
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;

    try {
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${stream.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Calendar file downloading!');
    } catch (error) {
      toast.error('Failed to download calendar file.');
    }
  };

  const openPurchaseModal = (stream, type = 'live') => {
    setPurchaseModal({ open: true, stream, type });
  };

  const handlePurchaseSuccess = () => {
    fetchStreams(); // Refresh to get updated purchase status
  };

  const handleReserve = async (stream) => {
    try {
      const res = await axios.post(`${API}/live-streams/${stream.id}/register`);
      toast.success(res.data?.message || 'Spot reserved!');
      fetchStreams();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not reserve a spot';
      toast.error(msg);
    }
  };

  // Render a stream card with paid stream handling
  const renderStreamCard = (stream, variant = 'default') => {
    const isPaid = stream.is_paid;
    const isPurchased = stream.is_purchased;
    const isLive = stream.is_live;
    const hasReplay = stream.has_replay;
    const isSoldOut = stream.is_sold_out;
    const isRegistered = stream.is_registered;
    const hasCapacity = !!stream.capacity;
    const purchasedLive = isPurchased && (stream.purchase_type || 'live') === 'live';
    // A session that has already happened: nothing live is left to sell or join
    const isPast = variant === 'recorded' && !isLive;
    const canViewLive = !isPaid || purchasedLive || user?.role === 'admin';
    const canViewReplay = !isPaid || isPurchased || user?.role === 'admin';
    // Admin may leave replay_price unset — fall back to the live price
    const replayPrice = Number(stream.replay_price) || Number(stream.live_price) || 0;

    return (
      <Card 
        key={stream.id} 
        data-testid={`stream-${stream.id}`}
        className={`hover-lift ${isLive ? 'border-2 border-red-400' : variant === 'upcoming' ? 'border-2 border-cyan-400/30' : ''}`}
      >
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <CardTitle className="text-xl flex items-center gap-2 flex-wrap">
                {tr(stream.title)}
                {isLive && <Badge className="bg-red-500">LIVE</Badge>}
                {isPaid && !isPurchased && (
                  <Badge className="bg-green-600">
                    <DollarSign className="w-3 h-3 mr-1" />
                    {isPast ? replayPrice.toFixed(2) : stream.live_price}
                  </Badge>
                )}
                {isPurchased && (
                  <Badge className="bg-cyan-600">
                    <Key className="w-3 h-3 mr-1" />
                    {purchasedLive ? 'Purchased' : 'Replay Purchased'}
                  </Badge>
                )}
                {hasReplay && (
                  <Badge variant="secondary">
                    <Play className="w-3 h-3 mr-1" />
                    Replay
                  </Badge>
                )}
              </CardTitle>
              <p className="text-sm text-gray-600 mt-2">
                <TruncatedText text={tr(stream.description)} maxLength={250} />
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Scheduled Date */}
          {stream.scheduled_date && (
            <div className="flex items-center gap-2 text-cyan-500 mb-4">
              <Clock className="w-4 h-4" />
              <span className="text-sm">
                {new Date(stream.scheduled_date).toLocaleDateString()} at {new Date(stream.scheduled_date).toLocaleTimeString()}
              </span>
              {stream.timezone && (
                <span className="text-xs text-gray-500">({stream.timezone})</span>
              )}
            </div>
          )}

          {/* Password Display for Purchased Streams */}
          {purchasedLive && stream.stream_password && (
            <div className="bg-slate-800 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-center gap-2">
                <Key className="w-5 h-5 text-cyan-400" />
                <span className="text-sm text-gray-400">Password:</span>
                <span className="text-lg font-mono font-bold text-cyan-400 tracking-wider">
                  {stream.stream_password}
                </span>
              </div>
            </div>
          )}

          {/* Video Embed - Only for users with access */}
          {!isViewOnly && canViewLive && stream.embed_url && (isLive || !stream.scheduled_date) && (
            <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
              <iframe
                src={stream.embed_url}
                className="w-full h-full"
                allowFullScreen
                title={stream.title}
              />
            </div>
          )}

          {/* Recent session with nothing attached yet — say so rather than show a bare card */}
          {!hasReplay && isPast && (
            <p className="text-xs text-gray-500 mb-4" data-testid={`stream-replay-pending-${stream.id}`}>
              <Clock className="w-3 h-3 inline mr-1" />
              The recording is being prepared — it will appear here and in the archive.
            </p>
          )}

          {/* Replay — only once the session has happened, opened in the player so
              uploaded files and provider links both work */}
          {hasReplay && isPast && (
            <div className="mb-4">
              {canViewReplay || stream.replay_is_free ? (
                <Button
                  onClick={() => setPlayingReplay(stream)}
                  className="w-full bg-gradient-to-r from-purple-600 to-cyan-600"
                  data-testid={`stream-watch-replay-${stream.id}`}
                >
                  <Play className="w-4 h-4 mr-2" /> Watch the recording
                </Button>
              ) : (
                <p className="text-xs text-gray-500" data-testid={`stream-replay-locked-${stream.id}`}>
                  <Lock className="w-3 h-3 inline mr-1" />
                  The recording is in the archive — buy the replay to watch it.
                </p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            {/* Capacity counter / Sold Out badge */}
            {hasCapacity && !isPast && (
              <div className="w-full mb-1">
                {isSoldOut ? (
                  <Badge className="bg-red-600 text-white" data-testid={`stream-soldout-${stream.id}`}>
                    <Lock className="w-3 h-3 mr-1" /> Sold Out
                  </Badge>
                ) : (
                  <Badge variant="outline" data-testid={`stream-spots-${stream.id}`}>
                    {Math.max(stream.capacity - (stream.registered_count || 0), 0)} of {stream.capacity} spots left
                  </Badge>
                )}
              </div>
            )}

            {/* Reserve a spot for FREE streams with capacity */}
            {!isPast && !isPaid && hasCapacity && !isViewOnly && (
              isRegistered ? (
                <Button disabled variant="outline" className="flex-1" data-testid={`stream-reserved-${stream.id}`}>
                  ✓ Spot Reserved
                </Button>
              ) : (
                <Button
                  onClick={() => handleReserve(stream)}
                  disabled={isSoldOut}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-purple-500"
                  data-testid={`stream-reserve-${stream.id}`}
                >
                  {isSoldOut ? 'Sold Out' : 'Reserve My Spot'}
                </Button>
              )
            )}

            {/* Purchase Buttons for Paid Streams */}
            {isPaid && !isPurchased && !isViewOnly && (
              <>
                {/* Live Purchase — seats only, so capacity applies */}
                {!isPast && !isSoldOut && (isLive || stream.scheduled_date) && (
                  <Button 
                    onClick={() => openPurchaseModal(stream, 'live')}
                    className="flex-1 bg-gradient-to-r from-cyan-500 to-purple-500"
                    data-testid={`stream-buy-live-${stream.id}`}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Buy Live Access - ${Number(
                      stream.sale_live_percent ? stream.sale_live_price : (stream.live_price || 0)
                    ).toFixed(2)}
                    {stream.sale_live_percent ? (
                      <span className="ml-2 text-xs font-normal opacity-80 line-through">
                        ${Number(stream.live_price || 0).toFixed(2)}
                      </span>
                    ) : null}
                  </Button>
                )}
                
                {/* Replay-only Purchase — any time, including pre-order and after sell-out,
                    but not for a past session whose recording has not landed yet */}
                {!(isPast && !hasReplay) && (
                  <Button
                    onClick={() => openPurchaseModal(stream, 'replay')}
                    variant="outline"
                    className="flex-1 border-purple-500 text-purple-700 hover:bg-purple-50"
                    data-testid={`stream-buy-replay-${stream.id}`}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {isPast ? 'Buy the Recording' : 'Buy Replay Only'} - ${Number(
                      stream.sale_replay_percent ? stream.sale_replay_price : replayPrice
                    ).toFixed(2)}
                    {stream.sale_replay_percent ? (
                      <span className="ml-2 text-xs font-normal text-gray-500 line-through">
                        ${Number(replayPrice).toFixed(2)}
                      </span>
                    ) : null}
                  </Button>
                )}
              </>
            )}

            {/* Sold-out notice for paid streams — live seats gone, replay still available */}
            {isPaid && !isPurchased && !isViewOnly && isSoldOut && !isPast && (
              <div className="w-full text-xs text-gray-500" data-testid={`stream-live-soldout-note-${stream.id}`}>
                <Lock className="w-3 h-3 inline mr-1" /> Live seats are sold out — the replay is still available.
              </div>
            )}

            {/* Join Stream Button */}
            {!isPast && canViewLive && stream.embed_url && !isViewOnly && (
              <Button
                onClick={() => window.open(stream.embed_url, '_blank')}
                className={`flex-1 ${isLive ? 'bg-gradient-to-r from-red-500 to-pink-500' : 'bg-gradient-to-r from-cyan-500 to-purple-500'}`}
              >
                {isLive ? 'Join Live Stream' : 'Open Stream'}
              </Button>
            )}

            {/* Add to Calendar */}
            {!isPast && stream.scheduled_date && !isViewOnly && (
              <Button
                onClick={() => addToCalendar(stream)}
                variant="outline"
                size="sm"
                className="text-cyan-600 border-cyan-600 hover:bg-cyan-50"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Add to Calendar
              </Button>
            )}
          </div>

          {/* Locked State for View-Only Users */}
          {isViewOnly && isPaid && (
            <div className="text-center py-4 text-gray-500">
              <Lock className="w-8 h-8 mx-auto mb-2" />
              <p>Upgrade to Gold or Platinum to purchase paid streams</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div className="text-center py-20"><TranslatableText text="Loading streams..." /></div>;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <TranslatableText as="h1" className="text-4xl sm:text-5xl font-bold text-white mb-3" text="Live Stream" />
          <TranslatableText as="p" className="text-lg text-gray-300" text="Join live sessions and watch recordings" />
        </div>
        <div className="flex flex-col gap-2">
          <Button
            onClick={() => navigate('/dashboard/replays')}
            variant="outline"
            className="border-purple-400/60 text-purple-200 hover:bg-purple-500/10"
            data-testid="open-replay-archive"
          >
            <Library className="w-4 h-4 mr-2" /> Replay Archive
          </Button>
          <Button
            onClick={() => navigate('/dashboard/replays?tab=library')}
            variant="outline"
            className="border-cyan-400/60 text-cyan-200 hover:bg-cyan-500/10"
            data-testid="open-my-replays"
          >
            <Play className="w-4 h-4 mr-2" /> My Replays
          </Button>
        </div>
      </div>

      {/* Membership Required Banner for Free Users */}
      {isViewOnly && (
        <Card 
          className="mb-6 border-2 border-yellow-500 bg-yellow-50 cursor-pointer hover:border-yellow-600 transition-all"
          onClick={() => setShowTierPaymentModal(true)}
        >
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Lock className="w-6 h-6 text-yellow-600" />
              <div>
                <h3 className="font-semibold text-yellow-900">Upgrade to Gold or Platinum - Click Here</h3>
                <p className="text-sm text-yellow-800">
                  Live stream access requires Gold or Platinum membership. Upgrade now to join live sessions and purchase premium content.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Live Now */}
      {liveStreams.length > 0 && (
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <h2 className="text-2xl font-bold text-white">Live Now</h2>
          </div>
          <div className="space-y-6">
            {liveStreams.map((stream) => renderStreamCard(stream, 'live'))}
          </div>
        </div>
      )}

      {/* Upcoming Streams */}
      {upcomingStreams.length > 0 && (
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-cyan-500" />
            <span className="text-cyan-500"><TranslatableText text="Upcoming Sessions" /></span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {upcomingStreams.slice((upcomingPage - 1) * PER_PAGE, upcomingPage * PER_PAGE).map((stream) => renderStreamCard(stream, 'upcoming'))}
          </div>
          <Pagination
            page={upcomingPage}
            totalPages={Math.max(1, Math.ceil(upcomingStreams.length / PER_PAGE))}
            onChange={setUpcomingPage}
            testidPrefix="upcoming-streams-pagination"
            theme="dark"
          />
        </div>
      )}

      {/* Recent Streams — the last two weeks; older ones live in the Replay Archive */}
      {recordedStreams.length > 0 && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Video className="w-6 h-6 text-purple-500" />
              <TranslatableText text="Recent Streams" />
            </h2>
            <button
              onClick={() => navigate('/dashboard/replays')}
              className="text-xs text-cyan-300/80 hover:text-cyan-100 transition-colors"
              data-testid="recorded-see-archive"
            >
              Older than two weeks? It's all in the archive →
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {recordedStreams.slice((recordedPage - 1) * PER_PAGE, recordedPage * PER_PAGE).map((stream) => renderStreamCard(stream, 'recorded'))}
          </div>
          <Pagination
            page={recordedPage}
            totalPages={Math.max(1, Math.ceil(recordedStreams.length / PER_PAGE))}
            onChange={setRecordedPage}
            testidPrefix="recorded-streams-pagination"
            theme="dark"
          />
        </div>
      )}

      {streams.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <Video className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No streams available yet. Check back soon!</p>
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      <TierPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        currentTier={user?.membership_tier || 'bronze'}
      />

      <TierPaymentModal
        isOpen={showTierPaymentModal}
        onClose={() => setShowTierPaymentModal(false)}
        onSuccess={() => {
          setShowTierPaymentModal(false);
          fetchStreams();
          fetchMembershipStatus();
        }}
        currentTier={membershipType}
        suggestedTier="gold"
      />

      <StreamPurchaseModal
        isOpen={purchaseModal.open}        onClose={() => setPurchaseModal({ open: false, stream: null, type: 'live' })}
        stream={purchaseModal.stream}
        purchaseType={purchaseModal.type}
        allowTypeSwitch={
          !purchaseModal.stream?.is_sold_out &&
          !!(purchaseModal.stream?.is_live || purchaseModal.stream?.scheduled_date)
        }
        onSuccess={handlePurchaseSuccess}
      />

      {playingReplay && (
        <ReplayPlayer
          replayId={playingReplay.id}
          title={playingReplay.title}
          onClose={() => setPlayingReplay(null)}
        />
      )}
    </div>
  );
};

export default LiveStreamPage;
